import { Thead, Tr, Td, Table as ChakraTable, HStack, VStack } from '@chakra-ui/react';
import { Button, TruncatableText } from 'Atoms/index.ts';
import { StateStatus } from 'Molecules/CardStatus/CardStatus.tsx';
import { InputCard } from 'Molecules/InputCard/InputCard.tsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FinancialTableColumnHeader } from './TableComponents/FinancialColumnHeader.tsx';
import { FinancialInput } from './TableComponents/FinancialInput.tsx';
import {
  allFinancialSectionKeys,
  useGetCompanyNotEligibleTableData,
} from './FinancialTables.hooks.ts';
import { ArrowCornerDownRight } from 'Tokens/Icons/Direction.tsx';
import { AttachmentDrawer } from 'Features/Screening/AttachmentsDrawer.tsx';
import { GetFinancialsDocumentationByIdDocument_ } from 'models/index.ts';

export const CompanyNotEligibleTable = () => {
  const { t } = useTranslation(['common', 'bUnits']);
  const {
    notEligibleFinancials,
    nonMaterialFinancials,
    currency,
    isLocked,
    toggleEstimateStatus,
    documentationData,
  } = useGetCompanyNotEligibleTableData();

  return (
    <AttachmentDrawer
      isOpen={documentationData.isAttachmentDrawerOpen}
      refetch={[GetFinancialsDocumentationByIdDocument_]}
      onClose={documentationData.onAttachmentDrawerClose}
      attachmentBox={documentationData.attachmentBox}
    >
      <InputCard
        documentation={documentationData.documentation}
        header={{
          title: t('bUnits:financials.notEligibleOrNotMaterialActivities'),
          subtitle: t('bUnits:financials.notEligibleOrNotMaterialActivitiesSubtitle'),
          status: notEligibleFinancials?.isEstimate ? StateStatus.todo : StateStatus.done,
          actions: isLocked
            ? []
            : [
                <Button
                  key={notEligibleFinancials?.id + 'mark-as-done'}
                  variant="ghost"
                  size="md"
                  onClick={() => {
                    toggleEstimateStatus(
                      [notEligibleFinancials?.id, nonMaterialFinancials?.id],
                      notEligibleFinancials?.isEstimate ?? false
                    );
                  }}
                >
                  {notEligibleFinancials?.isEstimate
                    ? t('common:actions.done')
                    : t('common:actions.edit')}
                </Button>,
              ],
        }}
        {...{ minWidth: '800px' }}
      >
        <ChakraTable>
          <Thead>
            <FinancialTableColumnHeader title={t('common:activity')} />
            {allFinancialSectionKeys.map((section) => (
              <FinancialTableColumnHeader
                title={t(`common:financials.${section}`)}
                helpLabel={t(`common:financials.${section}Tooltip`)}
                caption={currency}
              />
            ))}
          </Thead>
          <Tr>
            <Td>
              <TruncatableText
                text={t('bUnits:financials.notEligibleOrNotMaterialActivities')}
                variant="bodyStrong"
              />
            </Td>
            {allFinancialSectionKeys.map((section) => {
              return (
                <Td key={'NotEligible' + section?.valueOf()} padding="8px" textAlign="right">
                  <FinancialInput section={section} financialsId={notEligibleFinancials?.id} />
                </Td>
              );
            })}
          </Tr>
          <Tr>
            <Td>
              <HStack>
                <ArrowCornerDownRight />
                <VStack spacing={0}>
                  <TruncatableText
                    text={t('bUnits:financials.notMaterialActivities')}
                    variant="body"
                  />
                  <TruncatableText
                    text={t('bUnits:financials.notMaterialActivitiesSubtitle')}
                    variant="micro"
                    textColor="text.muted"
                  />
                </VStack>
              </HStack>
            </Td>
            {allFinancialSectionKeys.map((section) => {
              return (
                <Td key={'notMaterial' + section?.valueOf()} padding="8px" textAlign="right">
                  <FinancialInput section={section} financialsId={nonMaterialFinancials?.id} />
                </Td>
              );
            })}
          </Tr>
        </ChakraTable>
      </InputCard>
    </AttachmentDrawer>
  );
};
